// @ts-nocheck
import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

// SAIL Custom Code - Start
const normalizeKey = (v) => (v || '').trim().toLowerCase();
const normalizeDisplay = (v) => (v || '').trim();
const decodeJsonEscapes = (v) => {
  if (!v) return '';
  try {
    // Wrap as a JSON string so `\u003c` becomes `<`.
    return JSON.parse('"' + v.replace(/"/g, '\\"') + '"');
  } catch (_e) {
    return v;
  }
};

const STORAGE_KEY = 'selected_pickup_location_name';
const COOKIE_DAYS = 30;
const DOMAIN = '.sail.ca';

const getCookieDomainPart = () => {
  const host = window.location.hostname;

  // Only set cross-subdomain cookie in production
  if (host === 'sail.ca' || host.endsWith('.sail.ca')) {
    return '; Domain=.sail.ca';
  }

  return '';
};

const getCookie = (name) => {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\^+|])/g, '\\$1') + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const setCookie = (name, value, days = COOKIE_DAYS) => {
  if (!value) return;
  const maxAge = days * 24 * 60 * 60;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domainPart = getCookieDomainPart();

  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; Path=/${domainPart}; Max-Age=${maxAge}; SameSite=Lax${secure}`;
};
// SAIL Custom Code - End
class LocalPickup extends Component {
  /** @type {AbortController | undefined} */
  #activeFetch;

  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest(`.shopify-section, dialog`);

    /** @type {(event: VariantUpdateEvent) => void} */
    const variantUpdated = (event) => {
      if (event.detail.data.newProduct) {
        this.dataset.productUrl = event.detail.data.newProduct.url;
      }

      const variantId = event.detail.resource ? event.detail.resource.id : null;
      const variantAvailable = event.detail.resource ? event.detail.resource.available : null;
      if (variantId !== this.dataset.variantId) {
        if (variantId && variantAvailable) {
          this.removeAttribute('hidden');
          this.dataset.variantId = variantId;
          this.#fetchAvailability(variantId);
        } else {
          this.setAttribute('hidden', '');
        }
      }
    };

    closestSection?.addEventListener(ThemeEvents.variantUpdate, variantUpdated);

    // SAIL Custom Code - Start
    if (this.querySelector('.pickup-location__wrapper')) {
      this.#sortLocations();
    }
    this.#bindStoreSelection();

    if (!this.querySelector('.pickup-location__wrapper')) {
      const variantId = this.dataset.variantId;
      if (variantId) {
        this.#fetchAvailability(variantId);
      }
    }
    // SAIL Custom Code - End

    this.disconnectedCallback = () => {
      closestSection?.removeEventListener(ThemeEvents.variantUpdate, variantUpdated);
    };
  }

  // SAIL Custom Code - Start
  #bindStoreSelection() {
    const dialog = this.querySelector('dialog');
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[type="radio"][name^="pickup-location-"]')
    );

    if (!radios.length) return;

    const storedKey = normalizeKey(getCookie(STORAGE_KEY));
    const radioKeys = Array.from(radios).map((r) => normalizeKey(r.value));
    const defaultKey = normalizeKey(this.dataset.defaultLocation);

    const defaultRadioValue = defaultKey
      ? Array.from(radios).find((r) => normalizeKey(r.value) === defaultKey)?.value
      : null;

    const preferredValue =
      (storedKey &&
        radioKeys.includes(storedKey) &&
        Array.from(radios).find((r) => normalizeKey(r.value) === storedKey)?.value) ||
      defaultRadioValue ||
      radios[0].value;

    // Set cookie to default location if cookie is empty
    if (!storedKey && preferredValue) {
      setCookie(STORAGE_KEY, normalizeDisplay(preferredValue));
    }

    // Apply preferred selection
    for (const r of radios) {
      r.checked = normalizeKey(r.value) === normalizeKey(preferredValue);
    }

    // Update summary UI based on preferred selection
    this.#applySelectedStore(normalizeDisplay(preferredValue));

    // Listen changes
    for (const r of radios) {
      r.addEventListener('change', () => {
        const selected = normalizeDisplay(r.value);
        if (!selected) return;

        setCookie(STORAGE_KEY, selected);
        // this.#applySelectedStore(selected);
        updateAllLocalPickups();

        // Close the drawer/dialog
        const closeBtn = this.querySelector('.pickup-location__close-button');
        if (closeBtn) {
          closeBtn.click();
        } else if (dialog?.open) {
          dialog.close();
        }
      });
    }
  }

  open() {
    this.hydrate();

    const dialog = this.querySelector('dialog');
    if (dialog && !dialog.open) {
      dialog.showModal();
      // Close when clicking on the backdrop (outside the dialog content)
      // Use the theme's close button so the normal animation runs.
      dialog.addEventListener(
        'click',
        (evt) => {
          if (evt.target !== dialog) return;

          const closeBtn = this.querySelector('.pickup-location__close-button');
          if (closeBtn) {
            closeBtn.click();
          } else {
            dialog.close();
          }
        },
        { once: true },
      );
    }
  }

  hydrate() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    // If availability DOM is missing → fetch
    if (!this.querySelector('.pickup-location__wrapper')) {
      this.#fetchAvailability(variantId);
      return;
    }

    // Availability exists → ONLY re-apply stored selection
    const stored = normalizeDisplay(getCookie(STORAGE_KEY));
    if (stored) {
      this.#applySelectedStore(stored);
    }
  }

  refresh() {
    // Do nothing if radios are not present yet
    if (!this.querySelector('input[type="radio"][name^="pickup-location-"]')) {
      return;
    }

    this.#bindStoreSelection();

    const stored = normalizeDisplay(getCookie(STORAGE_KEY));
    if (stored) {
      this.#applySelectedStore(stored);
    }
  }

  applySelectedStoreFromGlobal() {
    const stored = normalizeDisplay(getCookie(STORAGE_KEY));
    if (!stored) return;

    this.#applySelectedStore(stored);
  }

  #applySelectedStore(locationName) {
    const nameKey = normalizeKey(locationName) || normalizeKey(getCookie(STORAGE_KEY));

    // Find the matching wrapper inside the dialog list
    const wrappers = this.querySelectorAll('.pickup-location__wrapper[data-location-name]');
    /** @type {HTMLElement | null} */
    let match = null;
    wrappers.forEach((w) => {
      const nKey = normalizeKey(w.getAttribute('data-location-name'));
      if (nKey === nameKey) match = /** @type {HTMLElement} */ (w);
    });

    // If we can't find a match (e.g. variant changed), fall back to first wrapper
    if (!match && wrappers.length) match = /** @type {HTMLElement} */ (wrappers[0]);

    const available = match?.getAttribute('data-location-available') === 'true';

    const resolvedName = normalizeDisplay(match?.getAttribute('data-location-name')) || normalizeDisplay(locationName);

    // Inject selected location name into the first summary column
    const selectedNameTarget = this.querySelector('[data-selected-pickup-location]');
    if (selectedNameTarget) {
      selectedNameTarget.textContent = resolvedName || normalizeKey(getCookie(STORAGE_KEY));
    }

    // Update the top summary paragraph
    const summaryP = this.querySelector('[data-pickup-available-text]');
    const status = match.getAttribute('data-location-status');

    if (summaryP) {
      if (available) {
        switch (status) {
          case 'stock':
            summaryP.innerHTML = `
          <span data-icon="available" class="availability-dot green-dot"></span>
          ${summaryP.dataset.pickupAvailableText}
        `;
            break;

          case 'low':
            summaryP.innerHTML = `
          <span data-icon="unavailable" class="availability-dot yellow-dot"></span>
          ${summaryP.dataset.pickupLowStockText}
        `;
            break;

          case 'oos':
            summaryP.innerHTML = `
          <span data-icon="unavailable" class="availability-dot red-dot"></span>
          ${summaryP.dataset.pickupNotAvailableText}
        `;
            break;
        }
      } else {
        summaryP.innerHTML = `
          <span data-icon="unavailable" class="availability-dot red-dot"></span>
          ${summaryP.dataset.pickupNotAvailableText}
        `;
      }
    }

    const statusP = this.querySelector('.pickup-location__status-line');

    if (statusP && match) {
      let html = '';

      switch (status) {
        case 'stock':
          html = `
        <span class="availability-dot green-dot"></span>
        ${decodeJsonEscapes(statusP.dataset.storeAvailableText)}
      `;
          break;

        case 'low':
          html = `
        <span class="availability-dot yellow-dot"></span>
        ${decodeJsonEscapes(statusP.dataset.storeLowStockText)}
      `;
          break;

        case 'oos':
          html = `
        <span class="availability-dot red-dot"></span>
        ${decodeJsonEscapes(statusP.dataset.storeNotAvailableText)}

      `;
          break;

        default:
          statusP.hidden = true;
          statusP.innerHTML = '';
          return;
      }

      statusP.innerHTML = html;
      statusP.hidden = false;
    }

    // Update radio state (in case called programmatically)
    const radios = this.querySelectorAll('input[type="radio"][name^="pickup-location-"]');
    radios.forEach((r) => {
      r.checked = normalizeKey(r.value) === normalizeKey(resolvedName);
    });
  }
  // SAIL Custom Code - End

  // SAIL Custom Code - Start
  #sortLocations() {
    const wrappers = Array.from(this.querySelectorAll('.pickup-location__wrapper[data-location-name]'));

    if (wrappers.length < 2) return;

    const parent = wrappers[0].parentElement;

    if (!parent) return;

    wrappers
      .sort((a, b) => {
        const nameA = a.getAttribute('data-location-name') || '';
        const nameB = b.getAttribute('data-location-name') || '';
        return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
      })
      .forEach((el) => parent.appendChild(el));
  }
  // SAIL Custom Code - End

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  #fetchAvailability = (variantId) => {
    if (!variantId) return;

    const abortController = this.#createAbortController();

    const url = this.dataset.productUrl;
    fetch(`${url}?variant=${variantId}&section_id=${this.dataset.sectionId}`, {
      signal: abortController.signal,
    })
      .then((response) => response.text())
      .then((text) => {
        if (abortController.signal.aborted) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        // SAIL Custom Code - Start
        const instanceId = this.dataset.instanceId;
        // SAIL Custom Code - End

        const wrapper = html.querySelector(`local-pickup[data-variant-id="${variantId}"]`);
        if (wrapper) {
          this.removeAttribute('hidden');
          morph(this, wrapper);

          // SAIL Custom Code - Start
          this.dataset.productUrl = url;
          // Sort locations alphabetically before re-binding
          this.#sortLocations();
          // Re-bind after DOM morph
          this.#bindStoreSelection();
          // Apply selected store AFTER availability DOM exists
          this.refresh();
          // SAIL Custom Code - End
        } else this.setAttribute('hidden', '');
      })
      .catch((_e) => {
        if (abortController.signal.aborted) return;
        this.setAttribute('hidden', '');
      });
  };
}

if (!customElements.get('local-pickup')) {
  customElements.define('local-pickup', LocalPickup);
}

// SAIL Custom Code - Start
const observeDialogOpen = () => {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'open' &&
        mutation.target instanceof HTMLDialogElement &&
        mutation.target.hasAttribute('open')
      ) {
        // Dialog just opened
        mutation.target.querySelectorAll('local-pickup').forEach((el) => {
          el.hydrate?.();
        });
      }
    }
  });

  document.querySelectorAll('dialog').forEach((dialog) => {
    observer.observe(dialog, { attributes: true });
  });
};

// Run once on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeDialogOpen);
} else {
  observeDialogOpen();
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-open-cart-pickup]');
  if (!btn) return;

  // Choose which pickup to open
  const pickups = document.querySelectorAll('local-pickup[data-instance-id^="cart-"]');
  if (!pickups.length) return;

  // Strategy: open the first cart item pickup
  pickups[0].open?.();
});

const updateAllLocalPickups = () => {
  document.querySelectorAll('local-pickup').forEach((el) => {
    el.applySelectedStoreFromGlobal?.();
  });
};

document.addEventListener(ThemeEvents.cartUpdate, () => {
  requestAnimationFrame(() => {
    document.querySelectorAll('local-pickup').forEach((el) => {
      el.hydrate?.();
    });
  });
});

// Guard to prevent observer-triggered recursion during hydration/morph
let isHydratingFromObserver = false;

// Update on discount
const observeCartMutations = () => {
  const cartRoot = document.querySelector('.cart-items__wrapper');
  if (!cartRoot) return;

  let rafId = null;

  const observer = new MutationObserver(() => {
    if (isHydratingFromObserver) return;

    if (rafId) cancelAnimationFrame(rafId);

    rafId = requestAnimationFrame(() => {
      isHydratingFromObserver = true;

      document.querySelectorAll('local-pickup').forEach((el) => {
        el.hydrate?.();
      });

      // Release the guard on the next frame
      requestAnimationFrame(() => {
        isHydratingFromObserver = false;
      });
    });
  });

  observer.observe(cartRoot, {
    childList: true,
    subtree: true,
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeCartMutations);
} else {
  observeCartMutations();
}
// SAIL Custom Code - End
